import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock, ArrowLeft } from "lucide-react";
import { apiLogin, setToken, formatApiError } from "../lib/api";

export default function AdminLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { token } = await apiLogin(email, password);
      setToken(token);
      toast.success("Signed in", { description: "Welcome back to Babul admin." });
      nav("/admin", { replace: true });
    } catch (err) {
      toast.error("Login failed", { description: formatApiError(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center px-4 py-10 relative">
      <div className="absolute inset-0 grid-overlay opacity-30 pointer-events-none" />
      <div className="w-full max-w-md relative">
        <a
          href="/"
          data-testid="admin-back-home"
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-navy mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Back to website
        </a>

        <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-8 lg:p-10">
          <div className="flex items-center gap-3 mb-8">
            <img src="/assets/babul-logo.jpeg" alt="Babul" className="w-12 h-12 rounded-full object-cover ring-1 ring-slate-200" />
            <div>
              <div className="font-display text-xl font-bold text-navy">Babul Admin</div>
              <div className="text-xs uppercase tracking-widest text-emerald font-semibold">
                Inquiry Dashboard
              </div>
            </div>
          </div>

          <h1 className="font-display text-2xl text-navy font-bold">Sign in to continue</h1>
          <p className="text-sm text-slate-600 mt-1">
            Enter your admin credentials to manage inquiries.
          </p>

          <form onSubmit={submit} className="mt-7 space-y-5" data-testid="admin-login-form">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Email</span>
              <input
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-login-email"
                autoComplete="email"
                className="mt-2 w-full px-4 py-3 rounded-xl bg-[#F7F9FC] border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald focus:border-transparent text-navy"
                placeholder="admin@babulcorp.com"
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Password</span>
              <input
                required
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-login-password"
                autoComplete="current-password"
                className="mt-2 w-full px-4 py-3 rounded-xl bg-[#F7F9FC] border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald focus:border-transparent text-navy"
                placeholder="••••••••"
              />
            </label>
            <button
              type="submit"
              disabled={busy}
              data-testid="admin-login-submit"
              className="w-full btn-primary disabled:opacity-70"
            >
              {busy ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Signing in…
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4" /> Sign in
                </>
              )}
            </button>
          </form>

          <p className="text-xs text-slate-400 text-center mt-6">
            Protected area · Babul Corporation
          </p>
        </div>
      </div>
    </div>
  );
}
